import React, { useEffect, useMemo, useRef, useState } from "react";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import { useLocation } from "react-router-dom";
import {
  fetchPublicOpsChat,
  postPublicOpsChatMessage,
  submitPublicOpsContact,
} from "./publicOpsContactApi";
import { resolveOpsContactSourceChannel } from "./resolveOpsContactSource";
import "./HotelsRiverOpsContactWidget.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHAT_POLL_MS = 3000;
const CHAT_TOKEN_STORAGE_KEY = "hr_ops_contact_chat_token";

function resolveSubmitError(err) {
  const serverError = err?.response?.data?.message;
  if (serverError) return String(serverError);
  if (err?.message) return String(err.message);
  return "לא ניתן היה לשלוח את הפנייה. נסו שוב בעוד רגע.";
}

function formatMsgTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function readStoredToken() {
  try {
    return String(sessionStorage.getItem(CHAT_TOKEN_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
}

function storeToken(token) {
  try {
    if (token) sessionStorage.setItem(CHAT_TOKEN_STORAGE_KEY, String(token));
    else sessionStorage.removeItem(CHAT_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export default function HotelsRiverOpsContactWidget() {
  const location = useLocation();
  const sourceChannel = useMemo(
    () => resolveOpsContactSourceChannel(location.pathname),
    [location.pathname],
  );
  const threadRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [chatToken, setChatToken] = useState("");
  const [messages, setMessages] = useState([]);
  const [closed, setClosed] = useState(false);
  const [draft, setDraft] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    message: "",
  });

  const inChat = Boolean(chatToken);

  const updateField = (key) => (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError("");
  };

  const scrollThreadToEnd = () => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const applyChatPayload = (payload) => {
    if (!payload || typeof payload !== "object") return;
    setMessages(Array.isArray(payload.messages) ? payload.messages : []);
    setClosed(Boolean(payload.closed));
  };

  const handleOpen = () => {
    setOpen(true);
    setError("");
    const stored = readStoredToken();
    if (stored && !chatToken) {
      setChatToken(stored);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewConversation = () => {
    storeToken("");
    setChatToken("");
    setMessages([]);
    setClosed(false);
    setDraft("");
    setError("");
    setForm({ full_name: "", phone: "", email: "", message: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const fullName = String(form.full_name || "").trim();
    const phone = String(form.phone || "").trim();
    const email = String(form.email || "").trim();
    const message = String(form.message || "").trim();

    if (!fullName) {
      setError("נא להזין שם מלא");
      return;
    }
    if (!phone && !email) {
      setError("נא להזין טלפון או אימייל");
      return;
    }
    if (email && !EMAIL_RE.test(email)) {
      setError("כתובת אימייל לא תקינה");
      return;
    }
    if (!message) {
      setError("נא לכתוב הודעה");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const data = await submitPublicOpsContact({
        full_name: fullName,
        phone: phone || undefined,
        email: email || undefined,
        message,
        source_channel: sourceChannel,
        source_url:
          typeof window !== "undefined" ? window.location.href : location.pathname,
      });
      const token = String(data?.chatToken || data?.chat_token || "").trim();
      if (!token) {
        setError("הפנייה נשלחה, אך לא ניתן לפתוח את הצ'אט כרגע.");
        return;
      }
      storeToken(token);
      setChatToken(token);
      if (data?.chat) applyChatPayload(data.chat);
      else setMessages([{ senderType: "visitor", message, isVisitor: true }]);
      setForm({ full_name: "", phone: "", email: "", message: "" });
    } catch (err) {
      setError(resolveSubmitError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendChat = async (event) => {
    event?.preventDefault?.();
    const text = String(draft || "").trim();
    if (!text || !chatToken || closed || sending) return;
    setSending(true);
    setError("");
    try {
      const data = await postPublicOpsChatMessage(chatToken, text);
      const msg = data?.message;
      if (msg) {
        setMessages((prev) => [...prev, msg]);
      }
      setDraft("");
    } catch (err) {
      setError(resolveSubmitError(err));
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (!open || !chatToken) return undefined;
    let cancelled = false;
    let timerId = 0;

    const poll = async () => {
      try {
        const data = await fetchPublicOpsChat(chatToken);
        if (!cancelled) applyChatPayload(data);
      } catch {
        /* keep last known messages while polling */
      } finally {
        if (!cancelled) {
          timerId = window.setTimeout(poll, CHAT_POLL_MS);
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [open, chatToken]);

  useEffect(() => {
    scrollThreadToEnd();
  }, [messages, open, inChat]);

  return (
    <div className="hr-ops-contact" lang="he">
      {!open ? (
        <button
          type="button"
          className="hr-ops-contact__fab"
          aria-label="פנייה לאופרציה"
          onClick={handleOpen}
        >
          <ChatBubbleOutline sx={{ fontSize: 26 }} aria-hidden />
        </button>
      ) : null}

      {open ? (
        <section className="hr-ops-contact__panel" aria-label="פנייה לאופרציה">
          <header className="hr-ops-contact__head">
            <div>
              <h2 className="hr-ops-contact__title">פנייה לאופרציה</h2>
              <p className="hr-ops-contact__subtitle">
                {inChat
                  ? "שיחה עם צוות האופרציה"
                  : "השאירו פרטים ונחזור אליכם בהקדם"}
              </p>
            </div>
            <div className="hr-ops-contact__head-actions">
              {inChat ? (
                <button
                  type="button"
                  className="hr-ops-contact__new-chat"
                  onClick={handleNewConversation}
                >
                  שיחה חדשה
                </button>
              ) : null}
              <button
                type="button"
                className="hr-ops-contact__close"
                aria-label="סגור"
                onClick={handleClose}
              >
                ×
              </button>
            </div>
          </header>

          <div className={`hr-ops-contact__body${inChat ? " hr-ops-contact__body--chat" : ""}`}>
            {inChat ? (
              <>
                <div
                  className="hr-ops-contact__thread"
                  ref={threadRef}
                  data-testid="hr-ops-contact-thread"
                >
                  {messages.length === 0 ? (
                    <p className="hr-ops-contact__thread-empty">השיחה נפתחה</p>
                  ) : (
                    messages.map((msg, index) => {
                      const isAdmin = Boolean(msg?.isAdmin || msg?.senderType === "admin");
                      const key = String(msg?.id || `${msg?.createdAt || ""}-${index}`);
                      return (
                        <div
                          key={key}
                          className={[
                            "hr-ops-contact__bubble",
                            isAdmin
                              ? "hr-ops-contact__bubble--ops"
                              : "hr-ops-contact__bubble--me",
                          ].join(" ")}
                        >
                          <div className="hr-ops-contact__bubble-meta">
                            <span>{isAdmin ? "צוות אופרציה" : "אתם"}</span>
                            <span>{formatMsgTime(msg?.createdAt)}</span>
                          </div>
                          <div className="hr-ops-contact__bubble-text">{msg?.message}</div>
                        </div>
                      );
                    })
                  )}
                </div>

                {closed ? (
                  <div className="hr-ops-contact__closed" role="status">
                    הפנייה נסגרה. אפשר לפתוח שיחה חדשה.
                  </div>
                ) : (
                  <form className="hr-ops-contact__composer" onSubmit={handleSendChat}>
                    <textarea
                      value={draft}
                      onChange={(e) => {
                        setDraft(e.target.value);
                        if (error) setError("");
                      }}
                      placeholder="כתבו הודעה..."
                      rows={2}
                      maxLength={4000}
                      disabled={sending}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void handleSendChat(e);
                        }
                      }}
                    />
                    <button type="submit" disabled={sending || !String(draft).trim()}>
                      {sending ? "שולחים…" : "שליחה"}
                    </button>
                  </form>
                )}

                {error ? (
                  <p className="hr-ops-contact__error" role="alert">
                    {error}
                  </p>
                ) : null}
              </>
            ) : (
              <form className="hr-ops-contact__form" onSubmit={handleSubmit}>
                <div className="hr-ops-contact__field">
                  <label htmlFor="hr-ops-contact-name">שם מלא</label>
                  <input
                    id="hr-ops-contact-name"
                    name="full_name"
                    value={form.full_name}
                    onChange={updateField("full_name")}
                    placeholder="לדוגמה: יעל כהן"
                    maxLength={200}
                    autoComplete="name"
                    disabled={submitting}
                  />
                </div>
                <div className="hr-ops-contact__field">
                  <label htmlFor="hr-ops-contact-phone">טלפון</label>
                  <input
                    id="hr-ops-contact-phone"
                    name="phone"
                    type="tel"
                    dir="ltr"
                    value={form.phone}
                    onChange={updateField("phone")}
                    placeholder="050-1234567"
                    maxLength={64}
                    autoComplete="tel"
                    disabled={submitting}
                  />
                </div>
                <div className="hr-ops-contact__field">
                  <label htmlFor="hr-ops-contact-email">אימייל</label>
                  <input
                    id="hr-ops-contact-email"
                    name="email"
                    type="email"
                    dir="ltr"
                    value={form.email}
                    onChange={updateField("email")}
                    placeholder="name@example.com"
                    maxLength={254}
                    autoComplete="email"
                    disabled={submitting}
                  />
                </div>
                <div className="hr-ops-contact__field">
                  <label htmlFor="hr-ops-contact-message">הודעה</label>
                  <textarea
                    id="hr-ops-contact-message"
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={updateField("message")}
                    placeholder="במה אפשר לעזור?"
                    maxLength={4000}
                    disabled={submitting}
                  />
                </div>

                {error ? (
                  <p className="hr-ops-contact__error" role="alert">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="hr-ops-contact__submit"
                  disabled={submitting}
                >
                  {submitting ? "שולחים…" : "שלחו פנייה לאופרציה"}
                </button>
              </form>
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}
